/**
 * `bool` and `enum` widgets: discrete controls, so each change is a complete
 * edit and commits at once. Mixed, a checkbox is indeterminate and a menu
 * shows a dash.
 */
import type { PropType } from "../../world/schema/propTypes";
import { FieldError, fieldAria } from "./fieldAria";
import { indeterminateRef, isMixed, MIXED_OPTION, MixedOption } from "./mixed";
import type { WidgetProps } from "./types";

export function BoolWidget({ id, label, prop, value, onChange, onCommit, error }: WidgetProps) {
  return (
    <span className="kn-field">
      <input
        ref={indeterminateRef(isMixed(value))}
        id={id}
        type="checkbox"
        className="kn-checkbox"
        aria-label={label}
        checked={value === true}
        disabled={prop.readonly === true}
        {...fieldAria(id, error)}
        onChange={() => {
          onChange(value !== true);
          onCommit();
        }}
      />
      <FieldError id={id} error={error} />
    </span>
  );
}

export function EnumWidget({ id, label, prop, value, onChange, onCommit, error }: WidgetProps) {
  const { options } = prop as Extract<PropType, { type: "enum" }>;
  return (
    <span className="kn-field">
      <select
        id={id}
        className="kn-input kn-select"
        aria-label={label}
        value={isMixed(value) ? MIXED_OPTION : typeof value === "string" ? value : ""}
        disabled={prop.readonly === true}
        {...fieldAria(id, error)}
        onChange={(event) => {
          onChange(event.target.value);
          onCommit();
        }}
      >
        <MixedOption mixed={isMixed(value)} />
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <FieldError id={id} error={error} />
    </span>
  );
}
